<template>
  <app-page-wrapper-grid class="order-confirmation__wrapper">
    <div class="hero-confirmation">
      <div v-if="activeVendor" class="order-confirmation p-t-5">
        <h1 class="order-confirmation__title">
          Thanks for ordering from:
          <p class="order-confirmation__vendor">{{ activeVendor.name }}</p>
        </h1>
      </div>
    </div>
    <div class="order-confirmation__collection">
      <div class="order-confirmation__code-id-container">
        <div class="order-confirmation__hero-details">
          <order-details v-if="!isScanEnabled" class="order-confirmation__container-order-details" />
          <!-- QR-code version 3 has 29x29 and for this reason the size is 203 (29*7) -->
          <qr-code v-if="isScanEnabled" class="order-confirmation__qr-code" :message="deviceOrderId" size="150" version="3" />
          <p class="order-confirmation__order-id-title">Order #{{ order.id }}</p>
        </div>
      </div>

      <div class="order-confirmation__footer">
        <div v-if="isScanEnabled" class="order-confirmation__order-details m-t-20">
          <order-details />
        </div>
        <shopping-cart-total class="order-confirmation__total m-t-20" :is-confirmation="true" :cart-items="cartItems" />
        <div class="order-confirmation__order-information m-t-20">
          <h2>Payment</h2>
          <div v-for="payment in order.payments" :key="payment.amount" class="order-confirmation__payment-method p-t-20">
            <template v-if="payment.paymentType === 1 && !!payment.cardNumber">
              <p class="order-confirmation__field cc-payment">{{ getCardNumber }}</p>
              <p class="order-confirmation__field">{{ getAmount(payment.amount) }}</p>
            </template>
            <template
              v-else-if="payment.paymentType === 7 || (payment.paymentType === 1 && !payment.cardNumber && payment.amount !== 0)"
            >
              <p class="order-confirmation__field ap-payment">Apple Pay</p>
              <p class="order-confirmation__field">{{ getAmount(payment.amount) }}</p>
            </template>
            <template v-else>
              <p class="order-confirmation__field tender-payment">
                {{ getTenderName(payment) }}
              </p>
              <p class="order-confirmation__field">{{ getAmount(payment.amount) }}</p>
            </template>
          </div>
        </div>
        <div class="order-confirmation__details m-t-20">
          <app-button
            text="Start a new order"
            :aria-label="'Start a new order'"
            outlined
            class="order-confirmation__new-order-button"
            :handler="routeToMenu"
          />
        </div>
      </div>
    </div>
  </app-page-wrapper-grid>
</template>

<!-- prettier-ignore -->
<script>

import { defineAsyncComponent } from "vue";
import ShoppingCartTotal from "../shopping-cart/components/cart-total/ShoppingCartTotal";
import OrderDetails from "../../components/order-details/OrderDetails";
import { mapActions, mapGetters, mapState } from "vuex";
import { roundOffByBankApproach, maskedCredential, OAPI_API_TYPE, ROUTE_NAMES, SENTRY_ERRORS } from "@/utils";
import pickupTimeMixin from "@/mixins/pickup-time.mixin";
import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";

export const QrCode = defineAsyncComponent(() => import("@/components/qr-code/QrCode"));

export default {
  name: "OrderConfirmation",
  components: {
    ShoppingCartTotal,
    QrCode,
    OrderDetails
  },
  mixins: [pickupTimeMixin],
  data() {
    return {
      order: Object,
      cartItems: Array,
      isOrderDetails: Boolean
    };
  },
  computed: {
    ...mapState("user", ["userData", "guestUserData"]),
    ...mapGetters({
      getVenueTimezone: "venue/getVenueTimezone",
      getDeviceOrderId: "cart/getDeviceOrderId",
      venueId: "venue/getActiveVenue",
      activeVendor: "vendor/getActiveVendor",
      getCheckoutParams: "cart/getCheckoutParams"
    }),
    getAmount() {
      return (amount) => amount && `$${roundOffByBankApproach(amount).toFixed(2)}`;
    },
    getTenderName() {
      return (payment) => {
        const cardName = payment?.selectedTender?.buttonText || payment?.selectedTender?.name || payment?.buttonText || "Card";
        const cardNumber =
          maskedCredential(payment?.card || payment?.cardNumber) || "****";
        const selectionName = payment?.selectedTender?.selectionName
        if (selectionName)
          return `${cardName} ${cardNumber} - ${selectionName}`
        return `${cardName} ${cardNumber}`;
      };
    },
    getCardNumber() {
      if (this.APIType === OAPI_API_TYPE) {
        const creditCard = this.order.payments.find((payment) => payment.paymentType === 1);
        const { cardNumber } = creditCard;

        const lastFour = cardNumber?.slice(cardNumber.length - 4) || "****";

        return `${creditCard?.cardType} **** ${lastFour}`;
      } else {
        return this.order.cardNumberLastFour ? `**** **** **** ${this.order.cardNumberLastFour}` : "";
      }
    },
    isScanEnabled() {
      return this.APIType === OAPI_API_TYPE && this.activeVendor?.scanToAddToCart && this.deviceOrderId;
    }
  },
  beforeUpdate: function () {
    if (!this.activeVendor)
      this.$router.push({
        name: ROUTE_NAMES.VENDOR_PICKER
      });
  },
  created() {
    this.order = this.getCheckoutParams.order;
    this.cartItems = this.getCheckoutParams.cartItems;
    this.deviceOrderId = this.getDeviceOrderId.toString();
    this.clearDeviceOrderId();

    this.$route.params.id ? (this.isOrderDetails = true) : (this.isOrderDetails = false);
    this.OAPI_API_TYPE = OAPI_API_TYPE;
  },
  async mounted() {
    try {
      await this.updateServerConfigWithMinimumTimePassed(this.venueId);
    } catch (error) {
      errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_UPDATING_SERVER_CONFIG, "OrderConfirmation", "mounted", {
        description: error
      });
    }
  },
  methods: {
    ...mapActions("cart", ["clearDeviceOrderId"]),
    ...mapActions("common", ["updateServerConfigWithMinimumTimePassed"]),
    makeReorder() {
      this.$router.push({
        name: ROUTE_NAMES.VENUE_CONTAINER,
        params: {
          venue: this.order.venueId
        }
      });
    },
    routeToMenu() {
      return this.$router.push({ name: ROUTE_NAMES.MENU, params: { vendor: this.activeVendor.id } });
    }
  }
};
</script>

<style scoped lang="scss">
.disabled-item {
  pointer-events: none;
}

.hidden {
  visibility: hidden;
}

.order-confirmation {
  max-width: 1250px;
  margin: auto;

  &__wrapper {
    max-width: initial;
  }

  &__title {
    text-align: center;
    color: #fff;
    font-size: 20px;
    margin: 16px 16px 0 16px;
    padding-bottom: 150px;
  }

  &__container {
    display: flex;
    justify-content: center;
  }

  &__subVendorListContainer {
    padding-top: 10px;
    width: 100%;
  }

  &__code-id-container {
    display: flex;
    width: 100%;
    justify-content: space-around;
    flex-direction: column;
    align-items: center;

    .order-details {
      width: auto;
      min-width: 200px;
    }
  }

  &__order-id-title {
    text-align: center;
    font-size: 10px;
  }

  &__qr-code {
    margin-bottom: 10px;
    max-height: 150px;
    max-width: 150px;
  }

  &__hero-details {
    margin-top: -120px;
    padding: 20px 20px 5px 20px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    border: solid 1px #e3e3e3;
  }

  &__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 20px;

    @include respond-to-mobile {
      align-items: center;
      margin: 0 16px;
    }
  }

  &__order-information {
    width: 486px;
    height: 100%;

    @include respond-to-mobile {
      margin-right: 0;
      display: block;
    }
  }

  &__payment-method {
    padding-bottom: 28px;
    display: flex;
    justify-content: space-between;
  }

  &__field {
    margin: 0;
  }

  &__details {
    width: 486px;
    display: flex;
    flex-direction: column;
  }

  &__order-details {
    display: flex;
    justify-content: center;
  }

  &__new-order-button {
    width: 344px;
    align-self: center;
    margin: 49px 0;
  }

  &__total,
  &__order-details {
    box-sizing: border-box;
    border-bottom: 1px solid $border-color;
    padding-bottom: 28px;
    width: 486px;
  }

  &__order-information,
  &__details,
  &__total,
  &__order-details {
    @include respond-to-mobile {
      margin-left: 0;
      width: 100%;
    }
  }

  &__vendor {
    margin: 5px;
  }
}

.shopping-cart-total h2 {
  font-size: 16px;
  color: #000;
}

.hero-confirmation {
  background-color: var(--group-1-color-1);
  margin: 0;
}

.order-details {
  width: 486px;
}
</style>
